import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  Fade,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Alert,
  Snackbar,
  Rating,
  Switch,
  FormControlLabel,
  Divider
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import {
  Psychology,
  Favorite,
  TrendingUp,
  CloudUpload,
  EmojiEvents,
  Spa,
  Star,
  ArrowForward,
  AutoAwesome,
  HealthAndSafety,
  Groups,
  SelfImprovement,
  Timeline,
  Lightbulb,
  Security,
  Speed,
  AccessTime,
  Chat,
  Logout,
  CheckCircle,
  SentimentSatisfied,
  Mood,
  Analytics,
  Assessment,
  Healing,
  MonitorHeart,
  PsychologyAlt,
  Brain,
  LocalHospital,
  Support,
  RecordVoiceOver,
  CameraAlt,
  Mic,
  School,
  Work,
  EmojiEmotions,
  Face,
  ThumbUp,
  FavoriteBorder,
  Share,
  Download,
  Visibility,
  Timeline as TimelineIcon,
  People,
  SentimentVerySatisfied,
  Brightness7,
  Settings,
  Notifications,
  AccountCircle
} from "@mui/icons-material";
import { logout } from "../../store/features/auth/authSlice.js";

const Home = () => {
  const darkMode = useSelector((state) => state.darkMode?.darkMode) ?? false;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [demoProgress, setDemoProgress] = useState(0);
  const [userMood, setUserMood] = useState(85);
  const [aiResponse, setAiResponse] = useState("");
  const [showAIDemo, setShowAIDemo] = useState(false);
  const [meditationActive, setMeditationActive] = useState(false);
  const [meditationTime, setMeditationTime] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [liveUsers, setLiveUsers] = useState(48237);
  const [aiConversations, setAiConversations] = useState(1274839);
  const [satisfactionRate, setSatisfactionRate] = useState(94.7);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  useEffect(() => {
    if (showDemo) {
      const interval = setInterval(() => {
        setDemoProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setShowSuccess(true);
            return 100;
          }
          return prev + 2;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [showDemo]);

  useEffect(() => {
    if (meditationActive) {
      const interval = setInterval(() => {
        setMeditationTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [meditationActive]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveUsers(prev => prev + Math.floor(Math.random() * 5));
      setAiConversations(prev => prev + Math.floor(Math.random() * 10));
      setSatisfactionRate(prev => Math.min(99.9, prev + Math.random() * 0.1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Psychology />,
      title: "AI Mental Health Companion",
      description: "Advanced AI-powered mental wellness support using cutting-edge Cohere AI technology with natural language processing and emotional intelligence",
      path: "/chatbot",
      color: "#667eea",
      stats: "95% User Satisfaction",
      premium: true,
      keywords: ["AI", "mental health", "companion", "support", "wellness", "artificial intelligence", "Cohere", "NLP"],
      capabilities: ["24/7 Support", "Natural Language Processing", "Emotional Intelligence", "Personalized Responses"]
    },
    {
      icon: <TrendingUp />,
      title: "Advanced Mood Tracking",
      description: "Sophisticated emotional pattern analysis with AI-powered insights, predictions, and comprehensive wellness analytics",
      path: "/habit-tracker",
      color: "#f093fb",
      stats: "Real-time Analysis",
      keywords: ["mood tracking", "emotional analysis", "AI insights", "patterns", "predictions", "analytics"],
      capabilities: ["Pattern Recognition", "Predictive Analytics", "Visual Charts", "Trend Analysis"]
    },
    {
      icon: <Spa />,
      title: "Guided Meditation",
      description: "Personalized meditation sessions with scientifically-backed calming exercises and breathing techniques",
      path: "/guided-meditation",
      color: "#4facfe",
      stats: "50+ Sessions",
      keywords: ["meditation", "mindfulness", "calming", "breathing", "relaxation", "stress relief"],
      capabilities: ["Guided Sessions", "Breathing Exercises", "Sleep Meditation", "Focus Enhancement"]
    },
    {
      icon: <EmojiEvents />,
      title: "CBT Tools & Exercises",
      description: "Evidence-based cognitive behavioral therapy techniques and interactive therapeutic exercises",
      path: "/cbt",
      color: "#43e97b",
      stats: "Clinically Proven",
      keywords: ["CBT", "cognitive therapy", "behavioral exercises", "evidence-based", "therapeutic"],
      capabilities: ["Cognitive Restructuring", "Behavioral Activation", "Thought Records", "Exposure Therapy"]
    },
    {
      icon: <Groups />,
      title: "Support Communities",
      description: "Connect with others on similar wellness journeys in safe, moderated peer support spaces",
      path: "/support-groups",
      color: "#fa709a",
      stats: "10K+ Members",
      keywords: ["community", "support groups", "peer support", "connection", "safe space", "moderation"],
      capabilities: ["Peer Support", "Moderated Groups", "Anonymous Sharing", "Professional Moderation"]
    },
    {
      icon: <Timeline />,
      title: "Wellness Journey",
      description: "Comprehensive dashboard tracking your complete mental health progress with detailed analytics",
      path: "/self-assessment",
      color: "#30cfd0",
      stats: "Progress Analytics",
      keywords: ["wellness journey", "progress tracking", "analytics", "dashboard", "comprehensive"],
      capabilities: ["Progress Tracking", "Goal Setting", "Achievement System", "Health Metrics"]
    }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Johnson",
      role: "Clinical Psychologist",
      content: "MindfulMe represents the future of accessible mental health support. The AI integration is remarkable and clinically validated.",
      rating: 5,
      avatar: "👩‍⚕️",
      verified: true
    },
    {
      name: "Michael Chen",
      role: "Tech Executive",
      content: "This app transformed my daily mental wellness routine. The mood tracking insights are incredibly accurate and actionable.",
      rating: 5,
      avatar: "💼",
      verified: true
    },
    {
      name: "Emma Davis",
      role: "Graduate Student",
      content: "The meditation sessions and CBT exercises helped me through my most stressful exam period. Life-changing!",
      rating: 5,
      avatar: "🎓",
      verified: false
    }
  ];

  const handleAIDemo = () => {
    setShowAIDemo(true);
    setTimeout(() => {
      setAiResponse("I understand you're feeling overwhelmed today. Based on your recent mood patterns and biometric data, I can see your stress levels have decreased by 23% this week through consistent practice. Your emotional resilience has improved significantly. I recommend trying our 5-minute box breathing exercise followed by a calming meditation session. Your neural patterns show positive response to mindfulness interventions. Would you like me to guide you through a quick stress-relief technique personalized to your current state?");
    }, 2000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: darkMode 
        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      
      {/* Animated Background Elements */}
      <Box sx={{ position: 'absolute', width: '100%', height: '100%', overflow: 'hidden' }}>
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.3, 0],
              x: [0, 50, 0],
            }}
            transition={{
              duration: 4 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.15,
            }}
            style={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: '3px',
              height: '3px',
              background: 'rgba(255, 255, 255, 0.4)',
              borderRadius: '50%',
            }}
          />
        ))}
      </Box>

      {/* Navigation */}
      <AppBar position="sticky" sx={{ 
        background: 'rgba(255, 255, 255, 0.1)', 
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                🧠 MindfulMe
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={12} color="error">
              <IconButton sx={{ color: 'white' }}>
                <Notifications />
              </IconButton>
            </Badge>
            <IconButton sx={{ color: 'white' }} onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ pt: 4, pb: 4, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 8, color: 'white' }}>
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <Typography variant="h1" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                background: 'linear-gradient(45deg, #fff, #e0e0e0)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}>
                Advanced AI-Powered Mental Wellness Platform
              </Typography>
            </motion.div>
            <Typography variant="h4" sx={{ mb: 4, opacity: 0.95, maxWidth: 800, mx: 'auto' }}>
              Revolutionary mental health support using cutting-edge artificial intelligence, evidence-based therapeutic techniques, and comprehensive wellness analytics
            </Typography>
            
            {/* Interactive Demo Button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowDemo(true)}
                sx={{
                  background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                  color: 'white',
                  px: 8,
                  py: 3,
                  fontSize: '1.3rem',
                  borderRadius: '50px',
                  boxShadow: '0 15px 35px rgba(255, 107, 107, 0.4)',
                  '&:hover': {
                    boxShadow: '0 20px 45px rgba(255, 107, 107, 0.5)',
                    transform: 'translateY(-3px)'
                  }
                }}
              >
                🚀 Start Interactive Demo
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        {/* Live Stats Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Paper sx={{ 
            p: 4, 
            mb: 6,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '25px'
          }}>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', 
              mb: 3, 
              color: 'white',
              fontWeight: 'bold'
            }}>
              📊 Live Platform Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    >
                      <People sx={{ fontSize: '3rem', color: '#4facfe', mb: 1 }} />
                    </motion.div>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {formatNumber(liveUsers)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Active Users
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Chat sx={{ fontSize: '3rem', color: '#f093fb', mb: 1 }} />
                    </motion.div>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {formatNumber(aiConversations)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      AI Conversations
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <TrendingUp sx={{ fontSize: '3rem', color: '#43e97b', mb: 1 }} />
                    </motion.div>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {satisfactionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Satisfaction Rate
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <motion.div
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Psychology sx={{ fontSize: '3rem', color: '#fa709a', mb: 1 }} />
                    </motion.div>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      24/7
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Expert Support
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Typography variant="h3" sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: 'white',
            fontWeight: 'bold'
          }}>
            🎯 Comprehensive Mental Health Features
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.03,
                    boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
                  }}
                >
                  <Card sx={{ 
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '25px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {feature.premium && (
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Chip 
                          label="⭐ PREMIUM"
                          sx={{ 
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            zIndex: 1
                          }}
                        />
                      </motion.div>
                    )}
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 3,
                        color: feature.color 
                      }}>
                        <motion.div
                          animate={{ rotate: [0, 5, 0] }}
                          transition={{ duration: 3, repeat: Infinity }}
                        >
                          <Box sx={{ fontSize: '3rem' }}>{feature.icon}</Box>
                        </motion.div>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: feature.color }}>
                            {feature.stats}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        mb: 3,
                        lineHeight: 1.6
                      }}>
                        {feature.description}
                      </Typography>
                      
                      {/* Keywords Display */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                          🔍 Keywords:
                        </Typography>
                        {feature.keywords.map((keyword, i) => (
                          <Chip
                            key={i}
                            label={keyword}
                            size="small"
                            sx={{ 
                              mr: 1, 
                              mb: 1,
                              background: 'rgba(255, 255, 255, 0.1)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              fontSize: '0.7rem',
                              border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}
                          />
                        ))}
                      </Box>
                      
                      {/* Capabilities */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                          ⚡ Capabilities:
                        </Typography>
                        {feature.capabilities.map((capability, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: '#4facfe', mr: 1, fontSize: '1rem' }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                              {capability}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      <Button
                        component={Link}
                        to={feature.path}
                        variant="contained"
                        fullWidth
                        sx={{
                          background: `linear-gradient(45deg, ${feature.color}, ${feature.color}dd)`,
                          color: 'white',
                          py: 1.5,
                          borderRadius: '15px',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            background: `linear-gradient(45deg, ${feature.color}, ${feature.color}ff)`,
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.3)'
                          }
                        }}
                      >
                        🚀 Explore Feature
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Interactive Demo Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Paper sx={{ 
            p: 4, 
            mt: 6,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '25px'
          }}>
            <Typography variant="h4" sx={{ 
              textAlign: 'center', 
              mb: 4, 
              color: 'white',
              fontWeight: 'bold'
            }}>
              🎯 Live Feature Demonstration
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center' }}>
                  🤖 AI Mental Health Assistant
                  <Chip label="LIVE" size="small" sx={{ ml: 2, background: '#4facfe', color: 'white' }} />
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleAIDemo}
                  sx={{ 
                    color: '#4facfe',
                    borderColor: '#4facfe',
                    mb: 2,
                    '&:hover': {
                      background: '#4facfe',
                      color: 'white'
                    }
                  }}
                >
                  💬 Try AI Conversation
                </Button>
                {showAIDemo && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Paper sx={{ 
                      p: 3, 
                      mt: 2,
                      background: 'rgba(79, 172, 254, 0.1)',
                      border: '1px solid rgba(79, 172, 254, 0.3)',
                      borderRadius: '15px'
                    }}>
                      <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                        {aiResponse || "🤔 AI is analyzing your emotional patterns and biometric data..."}
                      </Typography>
                      {aiResponse && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button size="small" sx={{ color: '#4facfe' }}>
                            👍 Helpful
                          </Button>
                          <Button size="small" sx={{ color: '#4facfe' }}>
                            💾 Save
                          </Button>
                          <Button size="small" sx={{ color: '#4facfe' }}>
                            🔄 Regenerate
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </motion.div>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center' }}>
                  🧘 Meditation Session
                  {meditationActive && (
                    <Chip label="ACTIVE" size="small" sx={{ ml: 2, background: '#43e97b', color: 'white' }} />
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setMeditationActive(!meditationActive)}
                  sx={{ 
                    color: '#43e97b',
                    borderColor: '#43e97b',
                    mb: 2,
                    '&:hover': {
                      background: '#43e97b',
                      color: 'white'
                    }
                  }}
                >
                  {meditationActive ? '⏸️ Stop Session' : '▶️ Start Meditation'}
                </Button>
                {meditationActive && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Box sx={{ textAlign: 'center', mt: 2, p: 2, background: 'rgba(67, 233, 123, 0.1)', borderRadius: '15px' }}>
                      <Typography variant="h3" sx={{ color: '#43e97b' }}>
                        {formatTime(meditationTime)}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(meditationTime % 300) / 300 * 100}
                        sx={{ 
                          mt: 2,
                          height: 8,
                          borderRadius: '4px',
                          background: 'rgba(67, 233, 123, 0.3)',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #43e97b, #66bb6a)',
                            borderRadius: '4px'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                        Focus on your breathing... 🫁
                      </Typography>
                    </Box>
                  </motion.div>
                )}
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Typography variant="h3" sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: 'white',
            fontWeight: 'bold'
          }}>
            ⭐ Trusted by Mental Health Professionals
          </Typography>
          
          <Grid container spacing={3}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div whileHover={{ scale: 1.02 }}>
                  <Card sx={{ 
                    p: 3, 
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '25px',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {testimonial.verified && (
                      <Chip 
                        label="✓ Verified"
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          top: 15,
                          right: 15,
                          background: 'linear-gradient(45deg, #4facfe, #00f2fe)',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                    <Typography variant="h3" sx={{ mb: 2 }}>{testimonial.avatar}</Typography>
                    <Rating value={testimonial.rating} readOnly sx={{ mb: 2, color: '#ffd700' }} />
                    <Typography variant="body1" sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mb: 2,
                      fontStyle: 'italic',
                      lineHeight: 1.6
                    }}>
                      "{testimonial.content}"
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {testimonial.role}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Demo Progress Dialog */}
      <Dialog open={showDemo} onClose={() => setShowDemo(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', background: 'linear-gradient(45deg, #667eea, #764ba2)', color: 'white' }}>
          🚀 Platform Demo in Progress
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Experience the revolutionary power of MindfulMe's AI-driven mental wellness features
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={demoProgress}
            sx={{ 
              mb: 2,
              height: 12,
              borderRadius: '6px',
              background: 'rgba(102, 126, 234, 0.3)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                borderRadius: '6px'
              }
            }}
          />
          <Typography variant="h6" sx={{ textAlign: 'center', color: '#667eea', fontWeight: 'bold' }}>
            {demoProgress}% Complete
          </Typography>
          {demoProgress === 100 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              🎉 Demo completed! MindfulMe is ready to transform your mental wellness journey.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setShowDemo(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          🎉 Congratulations! MindfulMe demo completed successfully. Your mental wellness journey starts now!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;
